import { createPortal } from "react-dom";
import { X, BookOpen } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useMobile } from "../hooks/useMobile";

interface BusinessProcessesModalProps {
  domainName: string;
  detailInfo: string;
  isOpen: boolean;
  onClose: () => void;
}

export const BusinessProcessesModal = ({
  domainName,
  detailInfo,
  isOpen,
  onClose,
}: BusinessProcessesModalProps) => {
  const isMobile = useMobile();

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-lg shadow-xl ${
          isMobile ? "max-w-full w-full" : "max-w-4xl w-full"
        } max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div
          className={`${
            isMobile ? "p-3" : "p-4"
          } border-b border-neutral-border flex items-center justify-between bg-gradient-to-r from-primary/10 to-primary/5`}
        >
          <div className="flex items-center gap-3">
            <BookOpen className={`${isMobile ? "w-5 h-5" : "w-6 h-6"} text-primary`} />
            <h2
              className={`${
                isMobile ? "text-lg" : "text-xl"
              } font-bold text-neutral-text`}
            >
              Бизнес-процессы: {domainName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`${
              isMobile ? "p-1.5" : "p-2"
            } rounded hover:bg-neutral-light text-neutral-text/60 hover:text-primary transition-colors`}
          >
            <X className={isMobile ? "w-4 h-4" : "w-5 h-5"} />
          </button>
        </div>

        {/* Контент */}
        <div
          className={`${
            isMobile ? "p-4" : "p-6"
          } overflow-auto flex-1 bg-neutral-bg`}
        >
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="text-2xl font-bold text-neutral-text mb-4 mt-6 first:mt-0">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-xl font-bold text-neutral-text mb-3 mt-5 first:mt-0">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-lg font-semibold text-neutral-text mb-2 mt-4 first:mt-0">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="text-neutral-text/80 mb-3 leading-relaxed">{children}</p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc list-inside mb-3 space-y-1 text-neutral-text/80">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-inside mb-3 space-y-1 text-neutral-text/80">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="ml-4">{children}</li>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-neutral-text">{children}</strong>
                ),
                code: ({ children }) => (
                  <code className="bg-neutral-light px-1.5 py-0.5 rounded text-sm font-mono text-primary">
                    {children}
                  </code>
                ),
                hr: () => (
                  <hr className="my-6 border-neutral-border" />
                ),
              }}
            >
              {detailInfo}
            </ReactMarkdown>
          </div>
        </div>

        {/* Футер */}
        <div
          className={`${
            isMobile ? "p-3" : "p-4"
          } border-t border-neutral-border flex ${
            isMobile ? "flex-col gap-2" : "justify-end gap-2"
          } bg-white`}
        >
          <button
            onClick={onClose}
            className={`${
              isMobile ? "w-full px-3 py-2 text-sm" : "px-4 py-2"
            } bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors`}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
